import React, { useState, useEffect, useMemo } from 'react'
import { GetUserProfileOutput, getUserProfile } from '../../src/sdk/user'
import * as userAPI from '../../src/sdk/user'
import './Login.css'
import { useToken } from '../hooks/useToken'
import { useStateProxy } from 'use-state-proxy'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { translate, Locale, useLang } from '../components/Locale'
import LanguageOption from '../components/LanguageOption'
import { usePageLayout } from '../hooks/usePageLayout'
import {
  prevent_auto_complete_as_password,
  validatePassword,
} from '../utils/password'
import { formatError } from '../utils/error'

function Login() {
  // Memoize layout options to prevent infinite re-renders
  const layoutOptions = useMemo(
    () => ({
      showSidebar: true,
      showHeader: true,
      showFooter: true,
      title: {
        en: 'Login',
        zh_cn: '登录',
        zh_hk: '登入',
      },
    }),
    [],
  )

  // Login should show all navigation elements
  usePageLayout(layoutOptions)

  const state = useStateProxy({
    email: '',
    password: '',
    confirm_password: '',
    is_email_used: null as null | boolean,
    topup_value: 0,
    error: '',
    sent_reset_password_code: false,
    reset_password_passcode: '',
    reset_password_error: '',
  })

  useEffect(() => {
    if (state.reset_password_error.includes('HttpError(422)')) {
      // the passcode is expired or already used
      state.sent_reset_password_code = false
    }
  }, [state.reset_password_error])

  const passcode_not_ready =
    state.sent_reset_password_code && state.reset_password_passcode.length !== 6

  const should_confirm_password =
    !state.is_email_used || state.sent_reset_password_code

  const { token, setToken } = useToken()
  const [params] = useSearchParams()

  const [profile, setProfile] = useState<GetUserProfileOutput | null>(null)
  const { lang } = useLang()

  const title = translate(
    lang,

    state.sent_reset_password_code
      ? {
          en: 'Reset Password',
          zh_cn: '重置密码',
          zh_hk: '重置密碼',
        }
      : state.is_email_used
      ? {
          en: 'Login',
          zh_cn: '登录',
          zh_hk: '登入',
        }
      : {
          en: 'Login / Register',
          zh_cn: '登录 / 注册',
          zh_hk: '登入 / 註冊',
        },

    // {
    //   en: mode === 'login' ? 'Login' : 'Login / Register',
    //   zh_cn: mode === 'login' ? '登录' : '登录 / 注册',
    //   zh_hk: mode === 'login' ? '登入' : '登入 / 註冊',
    // }
  )

  const passwordError = validatePassword(lang, state.password)
  const password_matched = state.password === state.confirm_password

  const error =
    state.error ||
    (state.password ? passwordError : null) ||
    (should_confirm_password && state.confirm_password && !password_matched
      ? translate(lang, {
          en: 'Passwords do not match',
          zh_cn: '密码不匹配',
          zh_hk: '密碼不匹配',
        })
      : null)

  const navigate = useNavigate()

  useEffect(() => {
    if (!state.email) return
    state.is_email_used = null
    userAPI
      .checkEmail({ query: { email: state.email } })
      .then(json => (state.is_email_used = json.is_registered))
  }, [state.email])

  useEffect(() => {
    if (token) {
      getUserProfile({}).then(setProfile)
    }
  }, [token])

  async function login() {
    try {
      let json = await userAPI.login({
        body: {
          email: state.email,
          password: state.password,
        },
      })
      setToken(json.token)
      state.error = ''
    } catch (error) {
      state.error = String(error)
    }
  }

  async function register() {
    let json = await userAPI.register({
      body: {
        email: state.email,
        password: state.password,
        lang,
      },
    })
    setToken(json.token)
  }

  async function sendResetPasswordCode() {
    try {
      await userAPI.requestResetPassword({
        body: {
          email: state.email,
          lang,
        },
      })
      state.sent_reset_password_code = true
      state.reset_password_error = ''
      state.reset_password_passcode = ''
    } catch (error) {
      state.reset_password_error = String(error)
    }
  }

  async function submitNewPassword() {
    try {
      let json = await userAPI.verifyResetPassword({
        body: {
          email: state.email,
          pass_code: state.reset_password_passcode,
          new_password: state.confirm_password,
          lang,
        },
      })
      setToken(json.token || '')
      state.reset_password_error = ''
    } catch (error) {
      state.reset_password_error = String(error)
    }
  }

  useEffect(() => {
    if (token) {
      let page = params.get('page')
      let package_id = params.get('package')
      let plan_id = params.get('plan')
      console.log({ page, package_id })
      if (page === 'top-up' && package_id) {
        navigate(`/top-up?package=${package_id}`)
      } else if (page === 'subscription' && plan_id) {
        navigate(`/subscription?plan=${plan_id}`)
      } else {
        navigate('/profile')
      }
    }
  }, [token, params])

  function renderBody() {
    if (token) {
      return (
        <>
          <p>
            <Locale
              en="You are already logged in. Redirecting to the profile page..."
              zh_cn="您已登录，即将跳转至个人资料页面..."
              zh_hk="您已登入，即將跳轉至個人資料頁面..."
            />
          </p>
          <Link to="/profile">
            <button>
              <Locale
                en="Go to Profile"
                zh_cn="前往个人资料"
                zh_hk="前往個人資料"
              />
            </button>
          </Link>
        </>
      )
    }
    return (
      <>
        <form>
          <h1>{title}</h1>
          {prevent_auto_complete_as_password}
          <input
            type="text"
            autoComplete="email webauthn"
            name="email"
            onChange={event => (state.email = event.target.value)}
            placeholder={translate(lang, {
              en: 'Email',
              zh_cn: '邮箱',
              zh_hk: '電郵',
            })}
          />
          {prevent_auto_complete_as_password}
          <input
            type="text"
            autoComplete="passcode"
            name="reset_password_passcode"
            hidden={!state.sent_reset_password_code}
            onChange={event =>
              (state.reset_password_passcode = event.target.value)
            }
            placeholder={translate(lang, {
              en: 'Passcode',
              zh_cn: '验证码',
              zh_hk: '驗證碼',
            })}
          />
          <input
            hidden={passcode_not_ready}
            type="password"
            autoComplete={
              !should_confirm_password ? 'current-password' : 'new-password'
            }
            onChange={event => (state.password = event.target.value)}
            placeholder={translate(lang, {
              en: 'Password',
              zh_cn: '密码',
              zh_hk: '密碼',
            })}
          />
          {should_confirm_password ? (
            <input
              hidden={passcode_not_ready}
              type="password"
              autoComplete="new-password"
              value={state.confirm_password}
              onChange={event => (state.confirm_password = event.target.value)}
              placeholder={translate(lang, {
                en: 'Confirm Password',
                zh_cn: '确认密码',
                zh_hk: '確認密碼',
              })}
            />
          ) : null}
        </form>

        {!state.is_email_used ? (
          <>
            <button
              className="primary-button"
              disabled={
                state.password !== state.confirm_password || !!passwordError
              }
              onClick={register}
            >
              <Locale
                en="Login / Register"
                zh_cn="登录 / 注册"
                zh_hk="登入 / 註冊"
              />
            </button>
            <p className="privacy-notice">
              <Locale
                en="By signing up, you agree to our "
                zh_cn="注册即表示您同意我们的"
                zh_hk="註冊即表示您同意我們的"
              />
              <Link to="/privacy">
                <Locale
                  en="Privacy Policy"
                  zh_cn="隐私政策"
                  zh_hk="私隱政策"
                />
              </Link>
            </p>
          </>
        ) : !state.sent_reset_password_code ? (
          <div className="login-buttons">
            <button className="action-button" onClick={login}>
              <Locale en="Login" zh_cn="登录" zh_hk="登入" />
            </button>
            <button
              className="primary-button reset-password-button"
              onClick={sendResetPasswordCode}
            >
              <Locale en="Reset Password" zh_cn="重置密码" zh_hk="重置密碼" />
            </button>
          </div>
        ) : (
          <button
            className="action-button"
            onClick={submitNewPassword}
            disabled={!!passwordError || !password_matched}
          >
            <Locale
              en="Submit New Password"
              zh_cn="提交新密码"
              zh_hk="提交新密碼"
            />
          </button>
        )}
        {state.reset_password_error ? (
          <p className="error">{formatError(state.reset_password_error)}</p>
        ) : null}
        {error ? <p className="error">{formatError(error)}</p> : null}
      </>
    )
  }

  return (
    <>
      <div className="body">
        <LanguageOption />

        <div className="reg-container">{renderBody()}</div>
      </div>
    </>
  )
}

export default Login
